import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import './Toast.css';

const ToastContext = createContext(null);
const ICONS = { success: '✓', error: '✕', warn: '!', info: 'i' };

export function ToastProvider({ children, autoDismissMs = 4000 }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const remove = useCallback((id) => {
    setToasts(ts => ts.filter(t => t.id !== id));
  }, []);

  const push = useCallback((message, opts = {}) => {
    const id = ++idRef.current;
    const variant = opts.variant || 'info';
    const t = { id, message, title: opts.title, variant, duration: opts.duration ?? autoDismissMs };
    setToasts(prev => [...prev, t]);
    if (t.duration > 0) setTimeout(() => remove(id), t.duration);
    return id;
  }, [autoDismissMs, remove]);

  const api = {
    push,
    success: (msg, opts) => push(msg, { ...opts, variant: 'success' }),
    error:   (msg, opts) => push(msg, { ...opts, variant: 'error' }),
    warn:    (msg, opts) => push(msg, { ...opts, variant: 'warn' }),
    info:    (msg, opts) => push(msg, { ...opts, variant: 'info' }),
    dismiss: remove,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" role="region" aria-label="Notifications" aria-live="polite">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast--${t.variant}`} role={t.variant === 'error' ? 'alert' : 'status'}>
            <span className="toast__icon" aria-hidden="true">{ICONS[t.variant]}</span>
            <div className="toast__body">
              {t.title && <div className="toast__title">{t.title}</div>}
              <div>{t.message}</div>
            </div>
            <button type="button" className="toast__close" aria-label="Dismiss" onClick={() => remove(t.id)}>×</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Soft fallback so tests/components don't crash if provider is missing.
    return {
      push: () => {}, success: () => {}, error: () => {}, warn: () => {}, info: () => {}, dismiss: () => {},
    };
  }
  return ctx;
}

export default ToastProvider;
